import React, { useMemo, useState } from 'react';
import merge from 'lodash.merge';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Form, Formik, FormikHelpers } from 'formik';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';
import Button from '@material-ui/core/Button';
import CalcIcon from 'components/Icons/CalcIcon';
import CircularProgress from '@material-ui/core/CircularProgress';

import { getYearsAndMonthsFromLuxon, formatAmountValue } from 'helpers/helpers';
import FormNumberField from 'components/FormFields/FormNumberField';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import FormDateSelects from 'components/FormFields/FormDateSelects';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormTextField from 'components/FormFields/FormTextField';
import Switch from 'components/FormFields/Switch';
import InflationRateField from 'components/FormFields/InflationRateField';

import { DepositsStore } from 'stores/workflowStores/finances/depositsStore/depositsStore';
import {
  Deposit,
  DepositFrequency,
  DepositOptions,
  DepositType,
  getInitialValues,
} from 'stores/workflowStores/finances/depositsStore/model';
import useStyles from './styles/moduleStyles';
import { Currency } from 'enums';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface OwnProps {
  opened: boolean;
  handleClose: () => void;
  currency: Currency;
  data?: Deposit;
  options: DepositOptions;
  refresh: () => void;
  defaultInflation: number;
  defaultInflationType: string;
  plan?: boolean;
  calculateConditions: boolean;
  calculateInacc: boolean;
}

interface Stores {
  depositsStore: DepositsStore;
}

type Props = OwnProps & Stores;

const DepositModal: React.FC<Props> = ({
  opened,
  handleClose,
  currency,
  depositsStore,
  data,
  options,
  refresh,
  defaultInflation,
  defaultInflationType,
  plan,
  calculateConditions,
  calculateInacc,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { handleSubmit, calculating, calcResult, calcValue } = depositsStore;
  let formSubmitting = false;
  const [showDetail, setShowDetail] = useState(data?.inflation?.enabled);

  const translatedCurrency = useMemo(() => t(`currency_${currency}`), [currency, t]);

  const formKey = 'DepositForm';

  const minDate = useMemo(() => {
    const now = new Date();
    return {
      years: now.getFullYear(),
      months: now.getMonth() + 1,
    };
  }, []);

  const maxDate = useMemo(() => {
    return getYearsAndMonthsFromLuxon(
      DateTime.fromSQL(data?.type === DepositType.FUTURE ? options.maxDate : options.maxPeriodicUntil || ''),
    );
  }, [options.maxPeriodicUntil, options.maxDate, data]);

  const onSubmit = async (values: Deposit, formikHelpers: FormikHelpers<Deposit>) => {
    const ok = await handleSubmit(values, formikHelpers, data!.type);
    if (ok) {
      handleClose();
      refresh();
    }
  };

  // FIXME: roll into one
  const initialValues = getInitialValues(currency);
  initialValues.inflation = { enabled: false, rate: defaultInflation, valuation: false, type: defaultInflationType };

  return data ? (
    <Dialog maxWidth="md" open={opened}>
      <DialogHeader onClose={handleClose}>
        {t(`deposits_modalTitle${data.type === DepositType.EMPLOYER ? '_employer' : ''}`)}
      </DialogHeader>
      <DialogContent>
        <Formik initialValues={merge({}, initialValues, data)} onSubmit={onSubmit}>
          {(formikProps) => {
            formSubmitting = formikProps.isSubmitting;
            return (
              <FormWithoutEnterSubmit id={t(formKey)}>
                {data.type === DepositType.SINGLE || data.type === DepositType.REGULAR ? (
                  <Grid container spacing={4}>
                    <Grid item xs={6} md={4}>
                      <GridSubtitle noMargin>{t(`deposits_${data.type}`)}:</GridSubtitle>
                      <FormNumberField
                        label={t('deposits_value')}
                        name={`value.value`}
                        endAdornment={translatedCurrency}
                        fast={false}
                      />
                      {data.type === DepositType.REGULAR && (calcResult || calculating) && (
                        <>
                          <span
                            style={{ padding: '8px', display: 'inline-block' }}
                            dangerouslySetInnerHTML={{
                              __html: t(calculating ? 'deposits_calculating' : calcResult || '', {
                                interpolation: { escapeValue: false },
                                value: formatAmountValue(calcValue || 0, currency),
                              }),
                            }}
                          />
                          {!calculating && calculateInacc && (
                            <>
                              <br />
                              <span style={{ padding: '8px', display: 'inline-block' }}>
                                {t('deposits_calc_inaccTarget')}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                    {data.type === DepositType.REGULAR && (
                      <>
                        {plan && (
                          <Grid item xs={6} md={4}>
                            <GridSubtitle noMargin>&nbsp;</GridSubtitle>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={depositsStore.calculateRegularDeposit}
                              startIcon={
                                calculating ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <CalcIcon fontSize="medium" transparentBgr />
                                )
                              }
                              disabled={
                                !calculateConditions ||
                                calculating ||
                                !!calcResult ||
                                formikProps.values.inflation?.enabled
                              }
                            >
                              {t('deposits_calculate')}
                            </Button>
                          </Grid>
                        )}
                        <Grid item xs={6} md={4}>
                          <GridSubtitle noMargin>{t('general_until')}:</GridSubtitle>
                          <FormDateSelects name={`until`} minDate={minDate} maxDate={maxDate} fast={false} />
                        </Grid>
                      </>
                    )}
                  </Grid>
                ) : (
                  <Grid container>
                    {data.type !== DepositType.EMPLOYER && (
                      <Grid container item xs={12} spacing={4}>
                        <Grid item xs={12} md={6}>
                          <FormTextField name="title" label={t('deposits_title')} maxLength={50} />
                        </Grid>
                      </Grid>
                    )}

                    <Grid container item xs={12} spacing={4}>
                      <Grid item xs={6}>
                        <FormSelectField
                          name="frequency"
                          label={t('general_frequency')}
                          options={(data.type === DepositType.EMPLOYER
                            ? options.frequencyEmployer
                            : options.frequency
                          ).map((freq) => ({
                            value: freq,
                            label: t(`general_frequency_${freq}`),
                          }))}
                          fast={false}
                        />
                      </Grid>
                      <Grid container item xs={6} alignItems="center">
                        {formikProps.values.frequency === DepositFrequency.REPEAT_EVERY && (
                          <>
                            <Grid item xs={6}>
                              <FormSelectField
                                name="repeatYears"
                                label={t('general_Count')}
                                options={new Array(options.maxYears - options.minYears + 1).fill(0).map((year, i) => ({
                                  value: `${i + options.minYears}`,
                                  label: `${i + options.minYears}`,
                                }))}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              {t('general_repeatYears')}
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={4}>
                      <Grid item xs={12} md={6}>
                        <FormNumberField
                          name="value.value"
                          label={t('deposits_value')}
                          endAdornment={t(`currency_${currency}`)}
                        />
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={4}>
                      <Grid item xs={12} md={6}>
                        <GridSubtitle noMargin>
                          {t(`general_${formikProps.values.frequency === DepositFrequency.SINGLE ? 'When' : 'Since'}`)}
                        </GridSubtitle>
                        <FormDateSelects name={`since`} minDate={minDate} maxDate={maxDate} />
                      </Grid>

                      {((formikProps.values.frequency !== DepositFrequency.SINGLE &&
                        formikProps.values.frequency !== DepositFrequency.EMPTY) ||
                        data.type === DepositType.EMPLOYER) && (
                        <Grid item xs={12} md={6}>
                          <GridSubtitle noMargin>{t(`general_Until`)} </GridSubtitle>
                          <FormDateSelects name={`until`} minDate={minDate} maxDate={maxDate} fast={false} />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
                {![DepositType.SINGLE, DepositType.FUTURE_SINGLE, DepositType.EMPLOYER].includes(data.type) && (
                  <>
                    <Link
                      className={classes.detailLink}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        setShowDetail(!showDetail);
                      }}
                    >
                      <span className={classes.detailControl}>
                        {t('general_advanced')}
                        <DropDownIcon className={`${showDetail ? classes.shownIcon : ''}`} />
                      </span>
                    </Link>

                    <Collapse in={showDetail}>
                      <Grid container>
                        <Grid container item xs={12} spacing={0} />
                        <Grid container item xs={12} spacing={4}>
                          <Grid item xs={6}>
                            <Switch
                              name={`inflation.enabled`}
                              label={t('general_useInflation')}
                              tooltipContent={t('deposits_inflation_tooltip')}
                              afterChange={() => {
                                if (!formikProps.values.inflation?.enabled) {
                                  formikProps.setFieldValue('inflation.valuation', false);
                                  formikProps.setFieldValue('inflation.type', '');
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <InflationRateField
                              name="inflation.rate"
                              label={t('general_annualInflation')}
                              {...(!formikProps.values.inflation?.enabled ? { disabled: true } : {})}
                            />
                          </Grid>
                          <Grid item xs={12} />
                          <Grid item xs={6}>
                            <Switch
                              name={`inflation.valuation`}
                              label={t('deposits_useInflation')}
                              tooltipContent={t('deposits_useInflation_tooltip')}
                              disabled={!formikProps.values.inflation?.enabled}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <FormSelectField
                              name="inflation.type"
                              options={options.inflationType.map((type) => ({
                                value: type,
                                label: t(`general_inflationType_${type}`),
                              }))}
                              fast={false}
                              disabled={
                                !formikProps.values.inflation?.enabled || !formikProps.values.inflation?.valuation
                              }
                              tooltipContent={t('deposits_useInflationType_tooltip')}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Collapse>
                  </>
                )}
              </FormWithoutEnterSubmit>
            );
          }}
        </Formik>
      </DialogContent>
      <DialogActions>
        <ControlButtons
          isSubmitting={formSubmitting}
          nextText={t('general_save')}
          form={formKey}
          nextButtonId="deposit-modal-submit"
          backText={t('general_cancel')}
          onBack={handleClose}
          onClick={() => {}}
        />
      </DialogActions>
    </Dialog>
  ) : null;
};

export default observer(DepositModal);
